import {otsBaseApi} from "../../ots-api";
import {TagDefinition, TagDefinitionUsage} from "../../../model/tag-definition";
import {removeUndefined} from "../../../util/util";

const ALL_TAG_TYPES = [
  "TagDefinitions",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

export const tagDefinitionsApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllTagDefinitions: builder.query<TagDefinition[], void>({
      query: (arg) => ({
        url: `/tags/definitions/all`,
        method: "GET",
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "TagDefinitions", id} as const)),
            {type: "TagDefinitions", id: "LIST"}
          ];
        } else {
          return [{type: "TagDefinitions", id: "LIST"}];
        }
      }
    }),
    getTagDefinitionUsage: builder.query<TagDefinitionUsage[], { objectTypes?: string[], limit?: number } | undefined>({
      query: (arg) => ({
        url: `/tags/definitions/most-used`,
        method: "GET",
        params: removeUndefined({
          objectTypes: arg?.objectTypes,
          limit: arg?.limit
        })
      })
    }),
    getTagDefinition: builder.query<TagDefinition, string>({
      query: (id) => ({
        url: `/tags/definitions/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "TagDefinitions", id}]
    }),
    createTagDefinition: builder.mutation<TagDefinition, Partial<TagDefinition>>({
      query: (mapping) => ({
        url: "/tags/definitions",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [
        {type: "TagDefinitions", id: "LIST"},
      ]
    }),
    updateTagDefinition: builder.mutation<TagDefinition, Partial<TagDefinition>>({
      query: (arg) => ({
        url: `/tags/definitions`,
        method: "PUT",
        body: arg,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "TagDefinitions", id},
        {type: "ObjectObjectChangeLogs", id},
        {type: "TagDefinitions", id: "LIST"},
      ]
    }),
    deleteTagDefinition: builder.mutation<void, Partial<TagDefinition>>({
      query: (mapping) => ({
        url: "/tags/definitions",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "TagDefinitions", id},
        {type: "TagDefinitions", id: "LIST"},
      ]
    }),
    addCategoryToTagDefinition: builder.mutation<void, { id: string, categoryId: string }>({
      query: ({id, categoryId}) => ({
        url: `/tags/definitions/${id}/categories/${categoryId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, {id}) =>
        [{type: "TagDefinitions", id: id}]
    }),
    removeCategoryFromTagDefinitions: builder.mutation<void, { id: string, categoryId: string }>({
      query: ({id, categoryId}) => ({
        url: `/tags/definitions/${id}/categories/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, {id}) =>
        [{type: "TagDefinitions", id: id}]
    }),
  })
});

export const {
  useGetAllTagDefinitionsQuery,
  useGetTagDefinitionUsageQuery,
  useGetTagDefinitionQuery,
  useCreateTagDefinitionMutation,
  useUpdateTagDefinitionMutation,
  useDeleteTagDefinitionMutation,
  useAddCategoryToTagDefinitionMutation,
  useRemoveCategoryFromTagDefinitionsMutation
} = tagDefinitionsApi;
